import { LowerCasePipe } from '@angular/common';
import { Component, computed, effect, inject } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatMenuModule } from '@angular/material/menu';
import { MatSortModule } from '@angular/material/sort';
import { MatTableModule } from '@angular/material/table';
import { HashtagComponent } from '@campaign-form/form-fields/creators-and-ctas/deliverable-flow-dialog/deliverable-step/deliverable-list/hashtag/hashtag.component';
import { AnalyticsDetailsTableComponent } from '@cta-analytics-dialog/deliverable-analytics-content/analytics-details-table/analytics-details-table.component';
import { DeliverableFindingsStore } from '@cta-analytics-dialog/deliverable-analytics-content/deliverable-findings.store';
import { EDeliverableType } from '@enums/campaign.enum';
import { ISocialPostApi } from '@interfaces/campaign-analytics-api.interface';
import { IHashTagAnalytics } from '@interfaces/deliverable-analytics.interface';
import { TranslateModule } from '@ngx-translate/core';
import { DotsSpinnerComponent } from '@shared/components/dots-spinner/dots-spinner.component';
import { IconComponent } from '@shared/components/icon/icon.component';
import { TStatusBadge, TStatusBadgeColorScheme } from '@shared/components/status-badge/status-badge.interface';
import { AnalyticsDetailsUtilitiesService } from '@utilities/analytics-details-utilities.service';
import { FindingVisualizerService } from '@utilities/finding-visualizer.service';
import { TextPostAnalyticsBaseDirective } from '../text-post-analytics-base/text-post-analytics-base.directive';
import { IHashtagPostAnalyticsTableData } from './hashtag-post-analytics.interface';

@Component({
  imports: [
    TranslateModule,
    AnalyticsDetailsTableComponent,
    MatTableModule,
    MatSortModule,
    MatButtonModule,
    HashtagComponent,
    LowerCasePipe,
    MatMenuModule,
    DotsSpinnerComponent,
    IconComponent,
  ],
  providers: [DeliverableFindingsStore],
  selector: 'app-hashtag-post-analytics',
  templateUrl: './hashtag-post-analytics.component.html',
})
export class HashtagPostAnalyticsComponent extends TextPostAnalyticsBaseDirective<
  IHashtagPostAnalyticsTableData,
  IHashTagAnalytics
> {
  public override readonly deliverableType = EDeliverableType.hashTag;
  public override readonly displayedColumns: string[] = ['text', 'actions'];
  public override readonly kpiCardData = null;

  public override readonly currentDeliverable = computed(() => {
    const deliverableId = this.deliverableId();
    const executionAnalytics = this.executionAnalyticsWithFindings();

    if (deliverableId && executionAnalytics) {
      return executionAnalytics?.ctaAnalytics?.hashtags?.find((h) => h.deliverableId === deliverableId);
    }

    return null;
  });
  public override readonly contentData = null;

  private readonly findingVisualizerService = inject(FindingVisualizerService);

  constructor() {
    super();
  }

  protected override setKeywordCounts(): void {
    const keywords = this.currentDeliverable()?.keywords?.map((k) => k.keyword) ?? [];
    const hashtags = (this.executionAnalyticsWithFindings()?.content?.socialContent as ISocialPostApi)?.hashtags ?? [];
    this.keywordCounts = AnalyticsDetailsUtilitiesService.getIndividualKeywordCounts(keywords, hashtags);

    AnalyticsDetailsUtilitiesService.mapKeywordCountsToDeliverableForm(this.deliverableForm(), this.keywordCounts);
  }

  protected override prepareTableData(): void {
    const keywords = this.currentDeliverable()?.keywords ?? [];
    const removedIds = this.manualFindingStatusStore?.serverRemovedIds() ?? new Set<string>();
    const visualizers = this.findingVisualizerService.buildFromKeywords(keywords, removedIds);

    const tableData: IHashtagPostAnalyticsTableData[] = visualizers.map((visualizer) => ({
      ...visualizer,
      text: visualizer.findings[0].text,
    }));

    this.dataSource.data = tableData;
    this.dataCount.set(tableData.length);
  }

  protected override setKeywordCardList(): void {
    const hashtag = this.currentDeliverable();

    if (hashtag?.keywords && hashtag.keywords.length) {
      const kpiCardList = hashtag.keywords.map((k) => {
        const badgeStatus: TStatusBadge = this.keywordCounts[k.keyword] ? 'found' : 'notFound';
        const badgeColorScheme: TStatusBadgeColorScheme = 'deliverable';

        return {
          badgeColorScheme,
          badgeStatus,
          icon: 'keyword.svg',
          id: `${k.keyword}-kpi-card`,
          keyword: k.keyword,
        };
      });

      this.keywordCardList.set(kpiCardList);
    }
  }

  protected override setKeywordsEffect(): void {
    effect(() => {
      const deliverableId = this.deliverableId();
      const executionAnalytics = this.executionAnalyticsWithFindings();

      if (deliverableId && executionAnalytics) {
        this.keywords = this.currentDeliverable()?.keywords?.map((k) => k.keyword) ?? [];
      }
    });
  }
}
